<template>
  <div class="director-menu">
    <!-- Root view: ephemeral message actions first, then persistent metadata
         attached to the drafted message. -->
    <div v-if="view === 'root'" class="director-menu__list">
      <button
        type="button"
        class="director-menu__item"
        :disabled="disabled || (!rewriteActive && !canRewrite)"
        @click="rewriteActive ? $emit('restore-original') : $emit('rewrite')"
      >
        <span class="director-menu__item-glyph" aria-hidden="true">
          <svg viewBox="0 0 16 16" fill="none">
            <path d="M3 5.5h7.5a2.5 2.5 0 0 1 0 5H8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" />
            <path d="M5 3.5 3 5.5l2 2M10.5 2.5l.45 1.05L12 4l-1.05.45-.45 1.05-.45-1.05L9 4l1.05-.45.45-1.05Z" fill="currentColor" />
          </svg>
        </span>
        <span class="director-menu__item-body">
          <span class="director-menu__item-label">
            {{ rewriteActive ? 'Use original draft' : 'Rewrite message' }}
          </span>
          <span
            class="director-menu__item-summary"
            :class="{ 'director-menu__item-summary--empty': !rewriteActive && !rewriteBotName }"
          >{{ rewriteSummary }}</span>
        </span>
        <span v-if="rewriteActive" class="director-menu__badge" aria-hidden="true" />
      </button>

      <button
        v-if="directorNotes"
        type="button"
        class="director-menu__item"
        :disabled="disabled"
        @click="openNote"
      >
        <span class="director-menu__item-glyph" aria-hidden="true">
          <svg viewBox="0 0 16 16" fill="none">
            <polygon points="8,2 14,6 14,10 8,14 2,10 2,6" stroke="currentColor" stroke-width="1.5" />
          </svg>
        </span>
        <span class="director-menu__item-body">
          <span class="director-menu__item-label">Director note</span>
          <span
            class="director-menu__item-summary"
            :class="{ 'director-menu__item-summary--empty': !hasNote }"
          >{{ note.trim() || 'Add instructions for the AI model, attached to the drafted message.' }}</span>
        </span>
        <span v-if="hasNote" class="director-menu__badge" aria-hidden="true" />
        <svg class="director-menu__chevron" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path d="M4.5 2.5L8 6l-3.5 3.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
      </button>
    </div>

    <!-- Note editor view: the director-note text plus its placement / expiry
         controls. Reached from the menu; the back arrow ascends to it. -->
    <div v-else class="director-menu__note">
      <div class="director-menu__note-header">
        <button
          type="button"
          class="director-menu__back"
          aria-label="Back to menu"
          @click="view = 'root'"
        >
          <svg viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path d="M7.5 2.5L4 6l3.5 3.5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <span class="director-menu__note-title">Director note</span>
      </div>

      <div class="director-menu__controls">
        <div class="director-menu__segment">
          <button
            type="button"
            :disabled="disabled"
            :class="segmentClass(placement === 'append')"
            @click="$emit('update:placement', 'append')"
          >
            Append
          </button>
          <button
            type="button"
            :disabled="disabled"
            :class="segmentClass(placement === 'prepend')"
            @click="$emit('update:placement', 'prepend')"
          >
            Prepend
          </button>
        </div>

        <div class="director-menu__expires">
          <span class="director-menu__field-label">Expires</span>
          <button
            type="button"
            role="switch"
            :aria-checked="expires ? 'true' : 'false'"
            :disabled="disabled"
            :class="['director-menu__switch', expires ? 'director-menu__switch--on' : '']"
            @click="$emit('update:expires', !expires)"
          >
            <span class="director-menu__switch-knob" :class="{ 'director-menu__switch-knob--on': expires }" />
          </button>
        </div>

        <div v-if="expires" class="director-menu__life">
          <span class="director-menu__field-label">Life</span>
          <div class="director-menu__stepper">
            <button
              type="button"
              :disabled="disabled || life <= 1"
              aria-label="Decrease life"
              @click="$emit('update:life', Math.max(1, life - 1))"
            >−</button>
            <span class="director-menu__life-value">{{ life }}</span>
            <button
              type="button"
              :disabled="disabled"
              aria-label="Increase life"
              @click="$emit('update:life', life + 1)"
            >+</button>
          </div>
        </div>
      </div>

      <textarea
        ref="noteTextareaRef"
        :value="note"
        :disabled="disabled"
        rows="2"
        :placeholder="notePlaceholder"
        class="director-menu__textarea"
        @input="$emit('update:note', ($event.target as HTMLTextAreaElement).value)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import type { DirectorNotePlacement } from '@/types/domain'

const props = withDefaults(defineProps<{
  note: string
  placement: DirectorNotePlacement
  expires: boolean
  life: number
  disabled?: boolean
  directorNotes?: boolean
  notePlaceholder?: string
  canRewrite?: boolean
  rewriteActive?: boolean
  rewriteStreaming?: boolean
  rewriteBotName?: string
}>(), {
  disabled: false,
  directorNotes: true,
  notePlaceholder: 'Write your note...',
  canRewrite: false,
  rewriteActive: false,
  rewriteStreaming: false,
  rewriteBotName: '',
})

defineEmits<{
  'update:note': [value: string]
  'update:placement': [value: DirectorNotePlacement]
  'update:expires': [value: boolean]
  'update:life': [value: number]
  rewrite: []
  'restore-original': []
}>()

const view = ref<'root' | 'note'>('root')
const noteTextareaRef = ref<HTMLTextAreaElement | null>(null)
const hasNote = computed(() => props.note.trim() !== '')
const rewriteSummary = computed(() => {
  if (props.rewriteActive) {
    return props.rewriteStreaming
      ? 'Stop the rewrite and return to your original message.'
      : 'Discard the AI rewrite and return to your original message.'
  }
  if (props.rewriteBotName) return `Rewrite with ${props.rewriteBotName}.`
  return 'Choose a global default bot to rewrite this message.'
})

function openNote() {
  if (props.disabled) return
  view.value = 'note'
}

function segmentClass(active: boolean) {
  return ['director-menu__segment-btn', active ? 'director-menu__segment-btn--active' : '']
}

// Drop focus onto the note as the editor view appears so typing is immediate.
watch(view, (next) => {
  if (next !== 'note') return
  nextTick(() => noteTextareaRef.value?.focus())
})
</script>

<style scoped>
.director-menu {
  padding: 0.625rem;
}

/* Root menu --------------------------------------------------------------- */
.director-menu__item {
  display: flex;
  width: 100%;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0.75rem;
  border-radius: 0.625rem;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: transparent;
  text-align: left;
  cursor: pointer;
  transition: background-color 150ms ease, border-color 150ms ease;
}

.director-menu__list {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
}

.director-menu__item:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.05);
}

.director-menu__item:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}

.director-menu__item-glyph {
  display: inline-flex;
  width: 1rem;
  height: 1rem;
  flex-shrink: 0;
  color: color-mix(in srgb, var(--theme-primary, rgb(29 138 255)) 65%, white);
}

.director-menu__item-glyph svg {
  width: 100%;
  height: 100%;
}

.director-menu__item-body {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
  gap: 1px;
}

.director-menu__item-label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: color-mix(in srgb, var(--theme-primary, rgb(29 138 255)) 70%, white);
}

.director-menu__item-summary {
  overflow: hidden;
  font-size: 0.8rem;
  text-overflow: ellipsis;
  /* white-space: nowrap; */
  color: var(--theme-on-surface, rgba(255, 255, 255, 0.78));
}

.director-menu__item-summary--empty {
  font-style: italic;
  color: var(--theme-on-surface-variant, rgba(255, 255, 255, 0.35));
}

.director-menu__badge {
  width: 6px;
  height: 6px;
  flex-shrink: 0;
  border-radius: 9999px;
  background: var(--theme-primary, rgb(29 138 255));
}

.director-menu__chevron {
  width: 0.75rem;
  height: 0.75rem;
  flex-shrink: 0;
  color: var(--theme-on-surface-variant, rgba(255, 255, 255, 0.3));
}

/* Note editor ------------------------------------------------------------- */
.director-menu__note-header {
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-bottom: 0.5rem;
}

.director-menu__back {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 0.5rem;
  border: 0;
  background: transparent;
  color: var(--theme-on-surface-variant, rgba(255, 255, 255, 0.6));
  cursor: pointer;
  transition: background-color 150ms ease, color 150ms ease;
}

.director-menu__back:hover {
  background: rgba(255, 255, 255, 0.08);
  color: var(--theme-on-surface, #fff);
}

.director-menu__back svg {
  width: 0.875rem;
  height: 0.875rem;
}

.director-menu__note-title {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: color-mix(in srgb, var(--theme-primary, rgb(29 138 255)) 70%, white);
}

.director-menu__controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
}

.director-menu__segment {
  display: flex;
  overflow: hidden;
  border-radius: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  font-size: 0.75rem;
  font-weight: 500;
}

.director-menu__segment-btn {
  padding: 0.25rem 0.625rem;
  background: transparent;
  border: 0;
  cursor: pointer;
  color: var(--theme-on-surface-variant, rgba(255, 255, 255, 0.6));
  transition: background-color 150ms ease, color 150ms ease;
}

.director-menu__segment-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.08);
  color: var(--theme-on-surface, #fff);
}

.director-menu__segment-btn--active {
  background: var(--theme-primary, rgb(29 138 255));
  color: var(--theme-on-primary, #fff);
}

.director-menu__segment-btn:disabled {
  cursor: not-allowed;
  opacity: 0.4;
}

.director-menu__expires,
.director-menu__life {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.director-menu__field-label {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--theme-on-surface-variant, rgba(255, 255, 255, 0.55));
  user-select: none;
}

.director-menu__switch {
  position: relative;
  width: 2.25rem;
  height: 1.25rem;
  flex-shrink: 0;
  border: 0;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.2);
  cursor: pointer;
  transition: background-color 200ms ease;
}

.director-menu__switch--on {
  background: var(--theme-primary, rgb(29 138 255));
}

.director-menu__switch-knob {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 1rem;
  height: 1rem;
  border-radius: 9999px;
  background: #fff;
  transition: transform 200ms ease;
}

.director-menu__switch-knob--on {
  transform: translateX(1rem);
}

.director-menu__stepper {
  display: flex;
  align-items: center;
  overflow: hidden;
  border-radius: 0.5rem;
  border: 1px solid rgba(255, 255, 255, 0.12);
  font-size: 0.75rem;
}

.director-menu__stepper button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.5rem;
  border: 0;
  background: transparent;
  color: var(--theme-on-surface-variant, rgba(255, 255, 255, 0.6));
  cursor: pointer;
  transition: background-color 150ms ease, color 150ms ease;
}

.director-menu__stepper button:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.08);
  color: var(--theme-on-surface, #fff);
}

.director-menu__stepper button:disabled {
  cursor: not-allowed;
  opacity: 0.3;
}

.director-menu__life-value {
  width: 2rem;
  padding: 0.125rem 0;
  border-left: 1px solid rgba(255, 255, 255, 0.1);
  border-right: 1px solid rgba(255, 255, 255, 0.1);
  text-align: center;
  font-variant-numeric: tabular-nums;
  color: var(--theme-on-surface, #fff);
}

.director-menu__textarea {
  display: block;
  width: 100%;
  box-sizing: border-box;
  min-height: 3.25rem;
  max-height: 9rem;
  resize: none;
  overflow-y: auto;
  padding: 0.5rem 0.625rem;
  border-radius: 0.625rem;
  border-left: 2px solid color-mix(in srgb, var(--theme-primary, rgb(29 138 255)) 40%, transparent);
  background: color-mix(in srgb, var(--theme-primary, rgb(29 138 255)) 5%, transparent);
  color: var(--theme-on-surface, #fff);
  font: 400 0.9rem/1.5 var(--story-font-family, Georgia, serif);
  outline: 0;
}

.director-menu__textarea::placeholder {
  color: var(--theme-on-surface-variant, rgba(255, 255, 255, 0.4));
}
</style>
